import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './StoringDetail.css';
import api from '../services/api';

const TYPE_LABEL = {
  werkorder: '🛠️ Werkorder / klus',
  restwerk: '📋 Restwerk',
  materiaal: '📦 Materiaal',
  idee: '💡 Idee',
};
const STATUS_KLEUR = {
  nieuw: '#3b82f6',
  in_behandeling: '#8b5cf6',
  afgehandeld: '#10b981',
  afgewezen: '#ef4444',
};

const MeldingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [melding, setMelding] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [afdelingen, setAfdelingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDoorsturenModal, setShowDoorsturenModal] = useState(false);
  const [selectedAfdeling, setSelectedAfdeling] = useState('');
  const [bezig, setBezig] = useState(false);
  const [showWerkorderModal, setShowWerkorderModal] = useState(false);
  const [woOrdernummer, setWoOrdernummer] = useState('');
  const [woTitel, setWoTitel] = useState('');
  const [woAfdelingen, setWoAfdelingen] = useState({});

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const res = await api.get(`/meldingen/${id}`);
      setMelding(res.data.melding);
      setFotos(res.data.fotos || []);
      try {
        const afdRes = await api.get('/afdelingen');
        setAfdelingen(afdRes.data.afdelingen || afdRes.data || []);
      } catch { setAfdelingen([]); }
      setLoading(false);
    } catch (error) {
      console.error('Fout bij laden:', error);
      setLoading(false);
    }
  };

  const handleStatus = async (status) => {
    setBezig(true);
    try {
      await api.put(`/meldingen/${id}/status`, { status });
      setMelding({ ...melding, status });
    } catch (e) { alert('Fout bij status wijzigen'); }
    setBezig(false);
  };

  const handleDoorsturen = async () => {
    if (!selectedAfdeling) { alert('Selecteer eerst een afdeling'); return; }
    setBezig(true);
    try {
      await api.post(`/meldingen/${id}/doorsturen`, { afdeling_id: parseInt(selectedAfdeling) });
      setShowDoorsturenModal(false);
      setSelectedAfdeling('');
      await loadData();
      alert('✅ Melding doorgestuurd!');
    } catch (e) { alert('Fout bij doorsturen'); }
    setBezig(false);
  };

  const toggleWoAfdeling = (afdId) => {
    setWoAfdelingen(prev => {
      const next = { ...prev };
      if (next[afdId]) delete next[afdId];
      else next[afdId] = { beschrijving: melding?.beschrijving || '' };
      return next;
    });
  };

  const updateWoAfdeling = (afdId, waarde) => {
    setWoAfdelingen(prev => ({ ...prev, [afdId]: { ...(prev[afdId] || {}), beschrijving: waarde } }));
  };

  const handleMaakWerkorder = () => { setWoTitel(melding?.titel || ''); setWoOrdernummer(''); setWoAfdelingen({}); setShowWerkorderModal(true); };

  const bevestigWerkorder = async () => {
    setBezig(true);
    try {
      const afdelingen = Object.keys(woAfdelingen).map(afdId => ({ afdeling_id: Number(afdId), beschrijving: woAfdelingen[afdId]?.beschrijving || '' }));
      const res = await api.post(`/meldingen/${id}/maak-werkorder`, { titel: woTitel, ordernummer: woOrdernummer || null, afdelingen });
      const woId = res.data?.werkorder?.id || res.data?.werkorder_id;
      setShowWerkorderModal(false);
      alert('✅ Werkorder aangemaakt!');
      if (woId) { navigate(`/werkorder/${woId}`); return; }
      await loadData();
    } catch (e) { alert('Fout bij aanmaken werkorder'); }
    setBezig(false);
  };

  const [bevestigVerwijderen, setBevestigVerwijderen] = useState(false);
  const handleVerwijderen = async () => {
    setBezig(true);
    try {
      await api.delete(`/meldingen/${id}`);
      alert('✅ Melding verwijderd');
      navigate('/meldingen');
    } catch (e) { alert('Fout bij verwijderen'); setBezig(false); }
  };

  const openInMaps = () => {
    if (melding.locatie_lat && melding.locatie_lng) {
      window.open(`https://www.google.com/maps?q=${melding.locatie_lat},${melding.locatie_lng}`, '_blank');
    } else if (melding.locatie_adres) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(melding.locatie_adres)}`, '_blank');
    }
  };

  if (loading) return <div className="loading-screen">Laden...</div>;
  if (!melding) return <div className="error-screen">Melding niet gevonden</div>;

  const alAfgehandeld = !!melding.werkorder_id;

  return (
    <div className="storing-detail">
      <div className="container">
        <div className="header">
          <button onClick={() => navigate('/meldingen')} className="btn-back">← Terug</button>
          <h1>📣 {melding.titel}</h1>
        </div>

        <div className="detail-grid">
          <div className="detail-main">
            <div className="info-card">
              <h3>Status wijzigen</h3>
              <div className="status-buttons">
                <button onClick={() => handleStatus('nieuw')} disabled={bezig} className={`btn-status ${melding.status === 'nieuw' ? 'active' : ''}`}>🆕 Nieuw</button>
                <button onClick={() => handleStatus('in_behandeling')} disabled={bezig} className={`btn-status ${melding.status === 'in_behandeling' ? 'active' : ''}`}>🔧 In behandeling</button>
                <button onClick={() => handleStatus('afgehandeld')} disabled={bezig} className={`btn-status ${melding.status === 'afgehandeld' ? 'active' : ''}`}>✅ Afgehandeld</button>
                <button onClick={() => handleStatus('afgewezen')} disabled={bezig} className={`btn-status ${melding.status === 'afgewezen' ? 'active' : ''}`}>❌ Afgewezen</button>
              </div>
            </div>

            <div className="info-card">
              <h3>📝 Beschrijving</h3>
              <p>{melding.beschrijving || '—'}</p>
            </div>

            <div className="info-card">
              <h3>ℹ️ Informatie</h3>
              <div className="info-rows">
                <div className="info-row"><span className="info-label">Type:</span><span>{TYPE_LABEL[melding.type] || melding.type}</span></div>
                {melding.object_naam && (
                  <div className="info-row"><span className="info-label">Object / wissel:</span><span>{melding.object_naam}{melding.object_plaats ? " \u00b7 " + melding.object_plaats : ""}</span></div>
                )}
                <div className="info-row"><span className="info-label">Gemeld door:</span><span>👤 {melding.gemeld_door_voornaam ? (melding.gemeld_door_voornaam + ' ' + (melding.gemeld_door_achternaam || '')) : 'Klantportaal'}</span></div>
                {melding.melder_afdeling_naam && (
                  <div className="info-row"><span className="info-label">Van afdeling:</span><span>🏢 {melding.melder_afdeling_naam}</span></div>
                )}
                <div className="info-row"><span className="info-label">Prioriteit:</span><span>{melding.prioriteit}</span></div>
                <div className="info-row">
                  <span className="info-label">Status:</span>
                  <span style={{ background: STATUS_KLEUR[melding.status] || '#6b7280', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>{melding.status}</span>
                </div>
                <div className="info-row"><span className="info-label">Gemeld op:</span><span>{new Date(melding.created_at).toLocaleString('nl-NL')}</span></div>
                {melding.doorgestuurd_afdeling_naam && (
                  <div className="info-row"><span className="info-label">Doorgestuurd naar:</span><span>➡️ {melding.doorgestuurd_afdeling_naam}</span></div>
                )}
                {melding.werkorder_titel && (
                  <div className="info-row"><span className="info-label">Werkorder:</span><span>📋 {melding.werkorder_titel}</span></div>
                )}
              </div>
            </div>

            {melding.locatie_adres && (
              <div className="info-card">
                <h3>📍 Locatie</h3>
                <p>{melding.locatie_adres}</p>
                {(melding.locatie_lat || melding.locatie_lng) && (
                  <small style={{ color: '#64748b', display: 'block', marginTop: '8px' }}>GPS: {melding.locatie_lat}, {melding.locatie_lng}</small>
                )}
                <button onClick={openInMaps} className="btn-maps">🗺️ Open in Google Maps</button>
              </div>
            )}

            {fotos.length > 0 && (
              <div className="info-card">
                <h3>📸 Foto's ({fotos.length})</h3>
                <div className="foto-grid">
                  {fotos.map(foto => (
                    <img
                      key={foto.id}
                      src={foto.url || `/uploads/meldingen/${foto.bestand_naam}`}
                      alt="Melding foto"
                      className="foto-thumb"
                      onClick={() => window.open(foto.url || `/uploads/meldingen/${foto.bestand_naam}`, '_blank')}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="detail-sidebar">
            <div className="info-card">
              <h3>🔧 Acties</h3>
              {afdelingen.length > 0 && (
                <button onClick={() => setShowDoorsturenModal(true)} className="btn-action" disabled={bezig}>📤 Doorsturen naar Afdeling</button>
              )}
              <button onClick={handleMaakWerkorder} className="btn-action" disabled={bezig || alAfgehandeld}>
                {alAfgehandeld ? '✅ Werkorder aangemaakt' : '📋 Maak Werkorder'}
              </button>
              {bevestigVerwijderen ? (
                <>
                  <button onClick={handleVerwijderen} className="btn-action" disabled={bezig} style={{ background: '#dc2626' }}>✓ Ja, verwijder</button>
                  <button onClick={() => setBevestigVerwijderen(false)} className="btn-action" disabled={bezig} style={{ background: '#6b7280' }}>✗ Nee</button>
                </>
              ) : (
                <button onClick={() => setBevestigVerwijderen(true)} className="btn-action" disabled={bezig} style={{ background: '#dc2626' }}>Verwijderen</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showWerkorderModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '12px', maxWidth: '540px', width: '90%' }}>
            <h3 style={{ marginBottom: '20px' }}>Werkorder aanmaken</h3>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '6px', color: '#374151' }}>Titel <span style={{ color: '#6b7280' }}>(geldt voor alle werkorders)</span></label>
            <input type="text" value={woTitel} onChange={(e) => setWoTitel(e.target.value)} autoFocus
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', marginBottom: '20px', fontSize: '16px', boxSizing: 'border-box' }} />
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '6px', color: '#374151' }}>Ordernummer <span style={{ color: '#6b7280' }}>(optioneel)</span></label>
            <input type="text" value={woOrdernummer} onChange={(e) => setWoOrdernummer(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', marginBottom: '20px', fontSize: '16px', boxSizing: 'border-box' }} />
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '6px', color: '#374151' }}>Afdeling(en) <span style={{ color: '#6b7280' }}>(1 werkorder per gekozen afdeling)</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
              {afdelingen.map(afd => {
                const actief = !!woAfdelingen[afd.id];
                return (
                  <button type="button" key={afd.id} onClick={() => toggleWoAfdeling(afd.id)}
                    style={{ padding: '8px 16px', borderRadius: '999px', border: actief ? '2px solid #2563eb' : '1px solid #d1d5db', background: actief ? '#dbeafe' : '#fff', color: actief ? '#1e3a8a' : '#374151', fontWeight: actief ? 600 : 500, cursor: 'pointer', fontSize: '14px' }}>
                    {actief ? '✓ ' : ''}{afd.naam}
                  </button>
                );
              })}
            </div>
            {Object.keys(woAfdelingen).map(afdId => {
              const afd = afdelingen.find(a => String(a.id) === String(afdId));
              if (!afd) return null;
              return (
                <div key={afdId} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '12px', background: '#f9fafb' }}>
                  <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1f2937' }}>📋 {afd.naam}</div>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#374151' }}>Beschrijving voor {afd.naam}</label>
                  <textarea value={woAfdelingen[afdId]?.beschrijving || ''} onChange={(e) => updateWoAfdeling(afdId, e.target.value)} rows="3"
                    placeholder={`Wat moet ${afd.naam} doen?`}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowWerkorderModal(false)} disabled={bezig}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>Annuleren</button>
              <button onClick={bevestigWerkorder} disabled={bezig || !woTitel.trim() || Object.keys(woAfdelingen).length === 0}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: (bezig || !woTitel.trim() || Object.keys(woAfdelingen).length === 0) ? '#9ca3af' : '#3b82f6', color: 'white', cursor: (bezig || !woTitel.trim() || Object.keys(woAfdelingen).length === 0) ? 'not-allowed' : 'pointer' }}>{bezig ? 'Bezig...' : (Object.keys(woAfdelingen).length > 1 ? `${Object.keys(woAfdelingen).length} Werkorders aanmaken` : 'Aanmaken')}</button>
            </div>
          </div>
        </div>
      )}

      {showDoorsturenModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '12px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ marginBottom: '20px' }}>📤 Doorsturen naar Afdeling</h3>
            <select value={selectedAfdeling} onChange={(e) => setSelectedAfdeling(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', marginBottom: '20px', fontSize: '16px' }}>
              <option value="">-- Selecteer afdeling --</option>
              {afdelingen.map(afd => <option key={afd.id} value={afd.id}>{afd.naam}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setShowDoorsturenModal(false); setSelectedAfdeling(''); }}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>Annuleren</button>
              <button onClick={handleDoorsturen} disabled={!selectedAfdeling}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: selectedAfdeling ? '#3b82f6' : '#d1d5db', color: 'white', cursor: selectedAfdeling ? 'pointer' : 'not-allowed' }}>Doorsturen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeldingDetail;
